import { api } from "@/lib/api";
import type {
    AnalyticsReportDto,
    DomainAvailabilityDto,
    DomainDto,
    DomainProvisioningDto,
    DomainStatus,
    PageResponse,
    RealtimeReportDto,
    RegistrarAccountDto,
} from "@/types/api";

export interface ListDomainsParams {
    status?: DomainStatus;
    page?: number;
    size?: number;
    sort?: string;
    /** Advertiser filter. -1 means "IS NULL" (unassigned). */
    advertiserId?: number;
}

export interface PurchaseDomainRequest {
    hostname: string;
    years?: number | null;
}

export interface AssignPageRequest {
    pageVersionId: number;
}

export interface UpdateDomainRequest {
    gaCode?: string | null;
    activePageVersionId?: number | null;
}

export async function checkAvailability(hostname: string): Promise<DomainAvailabilityDto> {
    const { data } = await api.get<DomainAvailabilityDto>("/admin/domains/check", {
        params: { hostname },
    });
    return data;
}

export async function listDomainsPaged(params: ListDomainsParams = {}): Promise<PageResponse<DomainDto>> {
    const q: Record<string, string | number> = {};
    if (params.status) q.status = params.status;
    if (params.page != null) q.page = params.page;
    if (params.size != null) q.size = params.size;
    if (params.sort) q.sort = params.sort;
    if (params.advertiserId != null) q.advertiserId = params.advertiserId;
    const { data } = await api.get<PageResponse<DomainDto>>("/admin/domains", { params: q });
    return data;
}

export async function listDomains(status?: DomainStatus): Promise<DomainDto[]> {
    const data = await listDomainsPaged({ status, size: 1000 });
    return data.content;
}

export async function getDomain(id: number): Promise<DomainDto> {
    const { data } = await api.get<DomainDto>(`/admin/domains/${id}`);
    return data;
}

export async function purchaseDomain(request: PurchaseDomainRequest): Promise<DomainDto> {
    const { data } = await api.post<DomainDto>("/admin/domains/purchase", request);
    return data;
}

export async function importDomain(hostname: string): Promise<DomainDto> {
    const { data } = await api.post<DomainDto>("/admin/domains/import", { hostname });
    return data;
}

export async function updateDomain(id: number, request: UpdateDomainRequest): Promise<DomainDto> {
    const { data } = await api.patch<DomainDto>(`/admin/domains/${id}`, request);
    return data;
}

export async function assignPage(id: number, pageVersionId: number): Promise<DomainDto> {
    const { data } = await api.post<DomainDto>(`/admin/domains/${id}/assign`, { pageVersionId });
    return data;
}

export async function retireDomain(id: number): Promise<DomainDto> {
    const { data } = await api.post<DomainDto>(`/admin/domains/${id}/retire`);
    return data;
}

/** Bring a RETIRED domain back into rotation (CF zone/worker survive retirement). */
export async function unretireDomain(id: number): Promise<DomainDto> {
    const { data } = await api.post<DomainDto>(`/admin/domains/${id}/unretire`);
    return data;
}

/** Provisioning history + whether a retry is available. */
export async function getDomainProvisioning(id: number): Promise<DomainProvisioningDto> {
    const { data } = await api.get<DomainProvisioningDto>(`/admin/domains/${id}/provisioning`);
    return data;
}

/** Resume a stalled/failed provisioning chain from the failed step (money-preserving). */
export async function retryProvisioning(id: number): Promise<DomainDto> {
    const { data } = await api.post<DomainDto>(`/admin/domains/${id}/retry-provisioning`);
    return data;
}

/** Force-republish this domain's edge (KV) config to Cloudflare. */
export async function resyncKv(id: number): Promise<DomainDto> {
    const { data } = await api.post<DomainDto>(`/admin/domains/${id}/resync-kv`);
    return data;
}

/** Registrar account snapshot (balance + fingerprint). Cached ~5 min server-side. */
export async function getRegistrarAccount(refresh = false): Promise<RegistrarAccountDto> {
    const { data } = await api.get<RegistrarAccountDto>("/admin/domains/registrar-account", {
        params: refresh ? { refresh: true } : {},
    });
    return data;
}

export async function reassignFresh(id: number): Promise<DomainDto> {
    const { data } = await api.post<DomainDto>(`/admin/domains/${id}/reassign-fresh`);
    return data;
}

/** Hard-delete a FAILED domain. Backend rejects anything in any other state. */
export async function deleteDomain(id: number): Promise<void> {
    await api.delete(`/admin/domains/${id}`);
}

export type AnalyticsRange = "24h" | "7d" | "30d" | "90d";

export async function getAnalytics(id: number, range: AnalyticsRange): Promise<AnalyticsReportDto> {
    const { data } = await api.get<AnalyticsReportDto>(`/admin/domains/${id}/analytics`, {
        params: { range },
    });
    return data;
}

export async function getAnalyticsRealtime(id: number): Promise<RealtimeReportDto> {
    const { data } = await api.get<RealtimeReportDto>(`/admin/domains/${id}/analytics/realtime`);
    return data;
}
